import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export interface HikeCar {
  _id: string;
  id?: string;
  userId: string;
  driverId: string;
  driverName?: string;
  driverEmail?: string;
  name: string;
  type: string;
  seats: number;
  numberPlate: string;
  createdAt?: string;
  updatedAt?: string;
}

interface HikeCarsState {
  list: HikeCar[];
  loading: boolean;
  error: string | null;
}

const getBaseApiUrl = () => {
  const isBrowser = typeof window !== "undefined";
  const isLocalhost =
    isBrowser &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const localBaseUrl = process.env.REACT_APP_BACKEND_URL;
  const publicBaseUrl =
    process.env.REACT_APP_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

  const normalizeBaseUrl = (value: string) => {
    const cleanBaseUrl = value.replace(/\/+$/, "");

    if (cleanBaseUrl.endsWith("/api/v1")) {
      return cleanBaseUrl;
    }

    return `${cleanBaseUrl}/api/v1`;
  };

  if (isLocalhost) {
    if (localBaseUrl?.trim()) {
      return normalizeBaseUrl(localBaseUrl.trim());
    }

    return "http://localhost:5000/api/v1";
  }

  if (publicBaseUrl?.trim()) {
    return normalizeBaseUrl(publicBaseUrl.trim());
  }

  if (
    localBaseUrl?.trim() &&
    !localBaseUrl.includes("localhost") &&
    !localBaseUrl.includes("127.0.0.1")
  ) {
    return normalizeBaseUrl(localBaseUrl.trim());
  }

  return "https://api.pickdengu.mw/api/v1";
};

const BASE_URL = `${getBaseApiUrl()}/hike-cars`;

const getCarId = (value: any) => String(value?._id || value?.id || "").trim();

const normalizeCar = (payload: any): HikeCar => {
  const source = payload?.car || payload?.data?.car || payload?.data || payload;
  const carId = getCarId(source);

  return {
    ...source,
    _id: carId,
    id: carId,
    userId: String(source?.userId || source?.driverId || "").trim(),
    driverId: String(source?.driverId || "").trim(),
    driverName: String(source?.driverName || "").trim() || undefined,
    driverEmail: String(source?.driverEmail || "").trim() || undefined,
    name: String(source?.name || "").trim(),
    type: String(source?.type || "").trim(),
    seats: Number(source?.seats || 0),
    numberPlate: String(source?.numberPlate || "").trim(),
  };
};

const normalizeCarsResponse = (payload: any): HikeCar[] => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.cars)
      ? payload.cars
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.cars)
          ? payload.data.cars
          : [];

  return source.map(normalizeCar).filter((car: any) => Boolean(car._id));
};

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (typeof error?.response?.data === "string") return error.response.data;
  if (error?.message) return error.message;

  return fallback;
};

const upsertCar = (cars: HikeCar[], incomingCar: HikeCar) => {
  const index = cars.findIndex((car) => car._id === incomingCar._id);

  if (index === -1) {
    cars.unshift(incomingCar);
    return;
  }

  cars[index] = incomingCar;
};

export const fetchHikeCars = createAsyncThunk<HikeCar[], void>(
  "hikeCars/fetch",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(BASE_URL);
      return normalizeCarsResponse(response.data);
    } catch (error) {
      console.error("Failed to fetch hike cars:", error);
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to fetch hike cars"),
      );
    }
  },
);

export const addHikeCar = createAsyncThunk<
  HikeCar,
  Omit<Partial<HikeCar>, "_id" | "id">
>("hikeCars/add", async (car, thunkAPI) => {
  try {
    const response = await axios.post(BASE_URL, car);
    return normalizeCar(response.data);
  } catch (error) {
    console.error("Failed to add hike car:", error);
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to add hike car"),
    );
  }
});

export const updateHikeCar = createAsyncThunk<
  HikeCar,
  { id: string; updates: Partial<HikeCar> }
>("hikeCars/update", async ({ id, updates }, thunkAPI) => {
  try {
    const response = await axios.put(`${BASE_URL}/${id}`, updates);
    return normalizeCar(response.data);
  } catch (error) {
    console.error("Failed to update hike car:", error);
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to update hike car"),
    );
  }
});

export const deleteHikeCar = createAsyncThunk<string, string>(
  "hikeCars/delete",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (error) {
      console.error("Failed to delete hike car:", error);
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to delete hike car"),
      );
    }
  },
);

const initialState: HikeCarsState = {
  list: [],
  loading: false,
  error: null,
};

const hikeCarsSlice = createSlice({
  name: "hikeCars",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHikeCars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHikeCars.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.list = action.payload;
      })
      .addCase(fetchHikeCars.rejected, (state, action) => {
        state.loading = false;
        state.error = String(action.payload || "Failed to fetch hike cars");
      })
      .addCase(addHikeCar.fulfilled, (state, action) => {
        state.error = null;
        upsertCar(state.list, action.payload);
      })
      .addCase(addHikeCar.rejected, (state, action) => {
        state.error = String(action.payload || "Failed to add hike car");
      })
      .addCase(updateHikeCar.fulfilled, (state, action) => {
        state.error = null;
        upsertCar(state.list, action.payload);
      })
      .addCase(updateHikeCar.rejected, (state, action) => {
        state.error = String(action.payload || "Failed to update hike car");
      })
      .addCase(deleteHikeCar.fulfilled, (state, action) => {
        state.error = null;
        state.list = state.list.filter((car) => car._id !== action.payload);
      })
      .addCase(deleteHikeCar.rejected, (state, action) => {
        state.error = String(action.payload || "Failed to delete hike car");
      });
  },
});

export default hikeCarsSlice.reducer;
